<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <!-- Desktop View -->
    <v-card v-if="!device.isMobile" flat class="company-card mt-6" outlined>
      <v-card-title>
        <v-row align="center" no-gutters>
          <v-col>
            <div class="text-center">
              <!-- Restaurant Name -->
              <div class="company-header mb-2">
                <h2 class="company-title">
                  {{ toLatin(selectedRestaurantName) }}
                </h2>
              </div>
              <!-- Period -->
              <div class="text-subtitle-1 grey--text text--darken-1">
                {{ periodText }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="employeeMealSummaryWithTotal"
          :search="search"
          :custom-filter="customSearch"
          hide-default-footer
          disable-pagination
          class="ordera-box-shadow striped-table"
          mobile-breakpoint="0"
        >
          <template v-slot:item.rb="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.rb }}
            </span>
          </template>

          <template v-slot:item.employee_name="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ toLatin(item.employee_name) }}
            </span>
          </template>

          <template v-slot:item.order_count="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.order_count }}
            </span>
          </template>

          <template v-slot:item.order_quantity="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.order_quantity }}
            </span>
          </template>

          <template v-slot:item.order_payed_quantity="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ item.order_payed_quantity }}
            </span>
          </template>

          <template v-slot:item.company_paid="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ (parseFloat(item.company_paid) || 0).toFixed(2) }}
            </span>
          </template>

          <template v-slot:item.employee_paid="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ (parseFloat(item.employee_paid) || 0).toFixed(2) }}
            </span>
          </template>

          <template v-slot:item.total="{ item }">
            <span :class="{ 'font-weight-bold': item.isTotal }">
              {{ (parseFloat(item.total) || 0).toFixed(2) }}
            </span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :title="selectedRestaurantName"
      :items="employeeMealSummaryWithTotal.filter((item) => !item.isTotal)"
      :card-config="mobileCardConfig"
      class="px-3"
    />
  </div>
</template>

<script>
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import limitService from "@/services/limit";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabEmployeeMealSummary",
  components: {
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    report: null,
    loading: false,
  }),
  computed: {
    search() {
      return this.$store.state.report.search;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    device() {
      return this.$store.state.device;
    },
    companyId() {
      return this.$store.state.company?.id || null;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    selectedRestaurantName() {
      if (this.selectedRestaurants.length === 1) {
        const restaurant = this.restaurants.find(
          (r) => r.id === this.selectedRestaurants[0]
        );
        return restaurant
          ? this.toLatin(restaurant.name)
          : this.$t("employee_meal_summary");
      }
      return this.$t("employee_meal_summary");
    },
    periodText() {
      const dateFrom = new Date(this.filters.dates.from);
      const dateTo = new Date(this.filters.dates.to);
      return `${this.formatDate(dateFrom)} - ${this.formatDate(dateTo)}`;
    },
    headers() {
      return [
        { text: this.$t("rb"), value: "rb", sortable: false, print: false },
        { text: this.$t("employee"), value: "employee_name" },
        { text: this.$t("number_of_orders"), value: "order_count", align: "center" },
        { text: this.$t("ordered"), value: "order_quantity", align: "center" },
        { text: this.$t("taken"), value: "order_payed_quantity", align: "center" },
        {
          text:
            this.$t("amount") +
            " - " +
            this.$t("company") +
            ` (${this.majorityRestaurantCurrency})`,
          value: "company_paid",
          align: "end",
        },
        {
          text:
            this.$t("amount") +
            " - " +
            this.$t("employee") +
            ` (${this.majorityRestaurantCurrency})`,
          value: "employee_paid",
          align: "end",
        },
        {
          text: this.$t("total") + ` (${this.majorityRestaurantCurrency})`,
          value: "total",
          align: "end",
        },
      ];
    },
    employeeMealSummary() {
      if (!this.report) return [];

      const userGroups = Array.isArray(this.report) ? this.report : [];

      const data = userGroups
        .map((group) => {
          const topUser = group.user || {};

          const txs = (group.limitTransactions || []).filter((t) => {
            if (t.company_id !== this.companyId) return false;
            if (t.orderable_id === null) return false;
            if (
              this.selectedRestaurants.length > 0 &&
              !this.selectedRestaurants.includes(t.restaurant_id)
            )
              return false;
            const txDate = (t.date || "").substring(0, 10);
            if (txDate < this.dates.from || txDate > this.dates.to) return false;
            return true;
          });

          if (txs.length === 0) return null;

          const userFromTx = txs[0].user;
          const userName = userFromTx
            ? [userFromTx.name, userFromTx.lastname].filter(Boolean).join(" ")
            : topUser.name || "";

          const orderCount = txs.length;
          const orderQuantity = txs.reduce((s, t) => s + (parseInt(t.quantity) || 0), 0);
          const orderPayedQuantity = txs.reduce(
            (s, t) => s + (parseInt(t.quantity_payed) || 0),
            0
          );
          const companyPaid = txs.reduce((s, t) => {
            const fromOrder = parseFloat(t.order?.company_paid_amount);
            if (fromOrder) return s + fromOrder;
            return s + Math.abs(parseFloat(t.total_amount ?? t.price) || 0);
          }, 0);
          const employeePaid = txs.reduce(
            (s, t) => s + (parseFloat(t.order?.user_paid_amount) || 0),
            0
          );
          const total = companyPaid + employeePaid;

          return {
            user_id: topUser.id,
            employee_name: this.toLatin(userName),
            order_count: orderCount,
            order_quantity: orderQuantity,
            order_payed_quantity: orderPayedQuantity,
            company_paid: companyPaid,
            employee_paid: employeePaid,
            total,
          };
        })
        .filter(Boolean);

      return data.sort((a, b) => a.employee_name.localeCompare(b.employee_name));
    },
    employeeMealSummaryWithTotal() {
      const numbered = this.employeeMealSummary.map((item, index) => ({
        ...item,
        rb: index + 1,
      }));

      // Calculate totals
      const totalOrderCount = numbered.reduce((sum, item) => sum + item.order_count, 0);
      const totalOrderQuantity = numbered.reduce(
        (sum, item) => sum + item.order_quantity,
        0
      );
      const totalOrderPayedQuantity = numbered.reduce(
        (sum, item) => sum + item.order_payed_quantity,
        0
      );
      const totalCompanyPaid = numbered.reduce((sum, item) => sum + item.company_paid, 0);
      const totalEmployeePaid = numbered.reduce(
        (sum, item) => sum + item.employee_paid,
        0
      );
      const grandTotal = totalCompanyPaid + totalEmployeePaid;

      return [
        ...numbered,
        {
          rb: this.$t("total").toUpperCase(),
          employee_name: "",
          order_count: totalOrderCount,
          order_quantity: totalOrderQuantity,
          order_payed_quantity: totalOrderPayedQuantity,
          company_paid: totalCompanyPaid,
          employee_paid: totalEmployeePaid,
          total: grandTotal,
          isTotal: true,
        },
      ];
    },
    mobileCardConfig() {
      return [
        { label: this.$t("employee"), value: "employee_name", primary: true },
        { label: this.$t("order_count"), value: "order_count" },
        { label: this.$t("ordered"), value: "order_quantity" },
        { label: this.$t("taken"), value: "order_payed_quantity" },
        {
          label: this.$t("company_paid"),
          value: "company_paid",
          format: (val) => this.formatCurrency(val),
        },
        {
          label: this.$t("employee_paid"),
          value: "employee_paid",
          format: (val) => this.formatCurrency(val),
        },
        {
          label: this.$t("total"),
          value: "total",
          format: (val) => this.formatCurrency(val),
        },
      ];
    },
  },
  methods: {
    async getUsers() {
      if (!this.companyId) {
        this.report = null;
        return;
      }

      this.loading = true;
      this.report = null;
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId, {
          date: this.dates.from,
          date2: this.dates.to,
        });
        this.report = data;
      } catch (error) {
        this.report = null;
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      if (value == null) return "—";
      return (
        new Intl.NumberFormat("sr-RS", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value) +
        " " +
        this.majorityRestaurantCurrency
      );
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("sr-RS");
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatSummaryForExport(item) {
      const moneyFields = ["company_paid", "employee_paid", "total"];
      const exportItem = {};
      this.getExportHeaders().forEach((h) => {
        exportItem[h.value] = moneyFields.includes(h.value)
          ? this.formatCurrency(item[h.value])
          : item[h.value] || "";
      });
      return exportItem;
    },
    customSearch(value, search, item) {
      return window.customSearch(value, search, item);
    },
  },
  watch: {
    companyId() {
      this.getUsers();
    },
    dates: {
      handler() {
        this.getUsers();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName:
          [this.$t("report"), this.$t("employee_meal_summary")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("employee_meal_summary"),
            headers: this.getExportHeaders(),
            items: this.employeeMealSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            title: this.toLatin(this.selectedRestaurantName),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.employeeMealSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            title: this.toLatin(this.selectedRestaurantName),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("employee_meal_summary")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.getUsers();
  },
};
</script>

<style scoped>
.company-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff 0%, rgba(0, 0, 0, 0.02) 100%);
}

.company-card:hover {
  border-color: rgba(25, 118, 210, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.company-header {
  position: relative;
}

.company-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
  letter-spacing: -0.5px;
}

@media (max-width: 768px) {
  .company-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 600px) {
  .company-card {
    border-radius: 8px;
  }

  .company-title {
    font-size: 1.3rem;
  }
}
</style>
